/**
 * Import dialog for exam results.
 *
 * Two source paths:
 *   - ``moodle_json``: file upload (default).
 *   - ``moodle_api``: only visible when the institution has a
 *     ``moodle_connections`` entry. Instead of a file upload, the
 *     Moodle quiz ID is captured; preview/commit go to
 *     `/import/api-preview` resp. `/import/api-commit`. Tiers without the
 *     driver (`evaluation_limits.drivers`, TF-1055) see the radio disabled
 *     with the target tier instead of a 402 after the quiz ID. A tier-quota
 *     402 renders the `auswertungen.tierBanner.*` sentence (see
 *     `translateQuotaError`), every other failure its `error_code` or the
 *     operation fallback — never `ApiError.message` (TF-772 PR 7).
 */

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Box,
  Typography,
  Alert,
  AlertTitle,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
} from '@mui/material';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';

import { appErrorFromApiError, translateError } from '../../errors';
import { ApiError, SubmissionsService } from '../../services/submissionsService';
import { MoodleConnectionsService } from '../../services/moodleConnectionsService';
import { isQuotaError, translateQuotaError } from './QuotaBanner';
import { EVALUATION_UPGRADE_TIER, TierChip, tierLabel } from './TierLockedHint';
import { useFeatures } from '../../hooks/useFeatures';
import { importRowErrorMessage } from './importRowErrorMessage';
import {
  DriverName,
  ImportJob,
  ImportJobStatus,
  ImportPreview,
  ImportRowError,
} from '../../types/submission';
import { useActivityHeartbeat } from '../../hooks/useActivityHeartbeat';

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  examId: number;
  examTitle: string;
  /**
   * Called only after a fully successful commit (status === 'succeeded')
   * so the caller can navigate or refresh. Partial / failed imports keep
   * the dialog open with an error so the user can read it before leaving.
   */
  onImported?: (job: ImportJob) => void;
  /**
   * Cadence for polling the async import job (TF-412). Defaults to 1.5 s;
   * exposed so tests can poll fast without fake timers.
   */
  pollIntervalMs?: number;
  /**
   * @deprecated TF-428: no longer used. The dialog now polls until the job
   * reaches a terminal status — the backend guarantees one via the import
   * task's hard ``time_limit`` plus the stuck-job reaper — so there is no
   * client-side cutoff that could time out before a long free-text grading
   * run finishes. The user can close the dialog to let the import continue in
   * the background. Kept optional so existing callers/tests still type-check.
   */
  pollTimeoutMs?: number;
}

type WizardStep = 'source' | 'upload' | 'preview' | 'submitting';

// 25 MB matches the backend MAX_UPLOAD_BYTES guard. Keeping the
// client-side limit equal means a too-large file fails fast in the
// browser without uploading.
const MAX_FILE_BYTES = 25 * 1024 * 1024;
const MAX_VISIBLE_ERRORS = 5;
const DEFAULT_POLL_INTERVAL_MS = 1500;

// The import commit is async (TF-412): the endpoint returns a ``queued``
// job and a Celery worker grades it. ``queued``/``running`` are transient;
// the dialog polls until the job reaches a terminal status (the set below).
const TERMINAL_STATUSES: readonly ImportJobStatus[] = [
  'succeeded',
  'partial',
  'failed',
];
const isTerminalStatus = (status: ImportJobStatus): boolean =>
  TERMINAL_STATUSES.includes(status);

// Promise that resolves after ``ms`` but rejects immediately on abort, so
// closing the dialog stops the poll loop without waiting out the interval.
const abortableDelay = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const id = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(id);
        reject(new DOMException('Aborted', 'AbortError'));
      },
      { once: true },
    );
  });

const ImportDialog: React.FC<ImportDialogProps> = ({
  open,
  onClose,
  examId,
  examTitle,
  onImported,
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
}) => {
  const { t } = useTranslation();

  // TF-838: Live-Activity presence for "exam_results_import" — only while
  // the dialog is actually open, not just mounted (the component stays
  // mounted across open/close, controlled by MUI's `open` prop).
  useActivityHeartbeat(open ? 'exam_results_import' : null);

  const [step, setStep] = useState<WizardStep>('source');
  const [driverName, setDriverName] = useState<DriverName>('moodle_json');
  const [file, setFile] = useState<File | null>(null);
  const [quizIdRaw, setQuizIdRaw] = useState('');
  const [hasMoodleConnection, setHasMoodleConnection] = useState<boolean | null>(
    null,
  );
  // ``null`` means "probe finished cleanly — either no permission or
  // no connection, both rendered as the existing 'unavailableTag'
  // chip". A non-null string means the probe itself errored
  // (network, 500, parse) and we surface it so the operator sees the
  // real cause instead of a silent disabled radio.
  const [moodleProbeError, setMoodleProbeError] = useState<string | null>(
    null,
  );
  // Unknown limits keep the radio up to the connection probe; the backend's
  // 402 stays the fallback.
  const { evaluationLimits } = useFeatures();
  const moodleApiLocked =
    evaluationLimits !== null && !evaluationLimits.drivers.includes('moodle_api');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState<ImportJob | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Abort any in-flight request when the dialog closes/unmounts so a
  // 25 MB upload doesn't keep streaming after the user leaves.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  // TF-336: On open, check whether the institution has a Moodle API
  // connection — only then is the ``moodle_api`` radio active.
  // A 403/permission error is expected (the teacher lacks
  // ``moodle:configure``) and is rendered as "no connection".
  // Other errors (network, 500, parse) must be surfaced, otherwise
  // the teacher just sees a disabled radio without knowing why.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setMoodleProbeError(null);
    MoodleConnectionsService.list()
      .then((res) => {
        if (cancelled) return;
        setHasMoodleConnection(res.items.length > 0);
      })
      .catch((err) => {
        if (cancelled) return;
        setHasMoodleConnection(false);
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          // Expected: caller has no ``moodle:configure``. Stay silent
          // and let the radio's existing "unavailable" chip explain.
          return;
        }
        setMoodleProbeError(
          translateError(
            appErrorFromApiError(err, 'moodle_connections_list_failed'),
            t,
            'auswertungen.importDialog.moodleProbeError',
          ),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open, t]);

  const resetState = () => {
    setStep('source');
    setDriverName('moodle_json');
    setFile(null);
    setQuizIdRaw('');
    setPreview(null);
    setError(null);
    setJob(null);
  };

  const handleClose = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    resetState();
    setBusy(false);
    onClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0] ?? null;
    if (next && next.size > MAX_FILE_BYTES) {
      setFile(null);
      setError(
        t('auswertungen.importDialog.fileTooLarge', {
          maxMb: Math.floor(MAX_FILE_BYTES / (1024 * 1024)),
          actualMb: (next.size / (1024 * 1024)).toFixed(1),
        }),
      );
      return;
    }
    setFile(next);
    setError(null);
  };

  // `message` is the caller's translateError(...) result, written out at the
  // call site so the fallback key stays a literal the i18n guard can see.
  //
  // It used to also render `ApiError.issues`, the per-row list the 422 carried
  // in `detail.issues`. TF-773 PR 2c ended that: the entries were built from
  // `exam_question_id` values of our own schema, and the whole failure is now
  // one coded sentence with the count in it. No import endpoint sends an
  // `issues` array any more, so that list is gone — not just unrendered.
  // `submissionsService.ApiError.issues` stays as a generic optional field on
  // the shared `ApiError` shape (unrelated to the separate `ApiError` class
  // in `activityService.ts`) — dead for this dialog specifically, kept only
  // because other `ApiError` consumers still declare it.
  const handleApiError = (err: unknown, message: string) => {
    // The 402 carries its code nested in `detail`, not as the ADR 0005
    // sibling field, so `appErrorFromApiError` cannot see it.
    setError(isQuotaError(err) ? translateQuotaError(err, t) : message);
  };

  const runPreview = async () => {
    setBusy(true);
    setError(null);
    abortRef.current = new AbortController();
    try {
      let result: ImportPreview;
      if (driverName === 'moodle_api') {
        const quizId = Number(quizIdRaw);
        if (!Number.isFinite(quizId) || quizId <= 0) {
          throw new Error(t('auswertungen.importDialog.errorPreview'));
        }
        result = await SubmissionsService.apiPreview({
          examId,
          quizId,
          signal: abortRef.current.signal,
        });
      } else {
        if (!file) return;
        result = await SubmissionsService.preview({
          examId,
          file,
          driverName,
          signal: abortRef.current.signal,
        });
      }
      setPreview(result);
      setStep('preview');
    } catch (err) {
      handleApiError(
        err,
        translateError(
          appErrorFromApiError(err, 'submissions_import_preview_failed'),
          t,
          'auswertungen.importDialog.errorPreview',
        ),
      );
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  // Poll the async import job until it reaches a terminal status, updating
  // the visible job state on each tick so the user sees live n/total progress.
  //
  // TF-428: no client-side deadline. A free-text-heavy import legitimately
  // runs for minutes, and the backend guarantees a terminal state (the task's
  // hard time_limit + the reap_stuck_import_jobs watchdog), so a wall-clock
  // cutoff here only ever produced a false "timeout" while the job actually
  // succeeded. The user can close the dialog at any point and the import keeps
  // running in the background; polling then stops via the abort signal.
  const pollUntilTerminal = async (
    jobId: number,
    signal: AbortSignal,
  ): Promise<ImportJob> => {
    for (;;) {
      await abortableDelay(pollIntervalMs, signal);
      const polled = await SubmissionsService.getImportJob(jobId);
      if (signal.aborted) {
        throw new DOMException('Aborted', 'AbortError');
      }
      setJob(polled);
      if (isTerminalStatus(polled.status)) {
        return polled;
      }
    }
  };

  const runCommit = async () => {
    setBusy(true);
    setStep('submitting');
    setError(null);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      let result: ImportJob;
      if (driverName === 'moodle_api') {
        const quizId = Number(quizIdRaw);
        result = await SubmissionsService.apiCommit({
          examId,
          quizId,
          signal: controller.signal,
        });
      } else {
        if (!file) return;
        result = await SubmissionsService.commit({
          examId,
          file,
          driverName,
          signal: controller.signal,
        });
      }
      setJob(result);

      // Commit only *enqueues* now (TF-412): the response is a queued job and
      // a Celery worker grades it. Poll until grading finishes (or fails). A
      // response that is already terminal — e.g. a fast worker, or a test mock
      // — skips the loop entirely.
      let finalJob = result;
      if (!isTerminalStatus(result.status)) {
        finalJob = await pollUntilTerminal(result.id, controller.signal);
      }

      // Only signal completion when nothing failed AND something was actually
      // persisted. partial / failed imports keep the dialog open so the user
      // reads the alert before the parent page navigates / reloads; TF-500: an
      // all-duplicates import (status succeeded, rows_processed === 0) does too,
      // so the "no new results" info is read instead of being skipped past on a
      // hollow success.
      if (finalJob.status === 'succeeded' && finalJob.rows_processed > 0) {
        onImported?.(finalJob);
      }
    } catch (err) {
      // Dialog closed mid-import: handleClose already aborted and ran
      // resetState, so any in-flight rejection must not re-pollute state for
      // the next open. The expected case is the AbortError; a real error that
      // merely lost the race to the abort is logged (not surfaced — the dialog
      // is gone) so it isn't completely invisible.
      if (controller.signal.aborted) {
        if (!(err instanceof DOMException && err.name === 'AbortError')) {
          console.warn('[ImportDialog] import error after dialog close:', err);
        }
        return;
      }
      handleApiError(
        err,
        translateError(
          appErrorFromApiError(err, 'submissions_import_commit_failed'),
          t,
          'auswertungen.importDialog.errorCommit',
        ),
      );
      setStep('preview'); // allow retry without re-uploading
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  const stepIndex = (
    {
      source: 0,
      upload: 1,
      preview: 2,
      submitting: 3,
    } as const
  )[step];

  const renderJobResult = (j: ImportJob) => {
    const skippedDuplicates = Number(
      (j.source_metadata?.attempts_skipped_idempotent as number | undefined) ??
        0,
    );
    // TF-500: a "successful" import that persisted nothing because every row
    // was an already-imported duplicate is shown as an informational outcome,
    // not a plain green success. "0 verarbeitet" under a success banner reads
    // as "nothing happened / silently broken" — exactly the confusion that hid
    // the cross-exam idempotency bug. This also covers the legitimate case of
    // re-importing the same file into the same exam.
    const allDuplicates =
      j.status === 'succeeded' &&
      j.rows_processed === 0 &&
      skippedDuplicates > 0;
    const severity = allDuplicates
      ? 'info'
      : j.status === 'succeeded'
        ? 'success'
        : j.status === 'partial'
          ? 'warning'
          : 'error';
    const errorRows: ImportRowError[] = (j.error_log ?? []).filter(
      (e): e is ImportRowError => Boolean(e),
    );
    return (
      <Alert severity={severity} data-testid="import-result">
        <AlertTitle>
          {allDuplicates
            ? t('auswertungen.importDialog.result.allDuplicates')
            : t(`auswertungen.importDialog.result.${j.status}`)}
        </AlertTitle>
        <Typography variant="body2">
          {allDuplicates
            ? t('auswertungen.importDialog.allDuplicatesSummary', {
                skipped: skippedDuplicates,
              })
            : t('auswertungen.importDialog.resultSummary', {
                processed: j.rows_processed,
                failed: j.rows_failed,
              })}
        </Typography>
        {errorRows.length > 0 && (
          <Box sx={{ mt: 1.5 }} data-testid="import-result-errors">
            <Typography variant="subtitle2">
              {/* Job-level entries only (row_index <= 0): nothing was skipped */}
              {errorRows.some((e) => e.row_index > 0)
                ? t('auswertungen.importDialog.errorListTitle')
                : t('auswertungen.importDialog.jobErrorListTitle')}
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {errorRows.slice(0, MAX_VISIBLE_ERRORS).map((e, idx) => (
                <li key={`${e.row_index}-${idx}`}>
                  {/* row_index 0/-1 marks a job-level entry — no row to name */}
                  {e.row_index > 0
                    ? t('auswertungen.importDialog.rowError', {
                        row: e.row_index,
                        reason: importRowErrorMessage(e, t),
                      })
                    : importRowErrorMessage(e, t)}
                </li>
              ))}
            </ul>
            {errorRows.length > MAX_VISIBLE_ERRORS && (
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.5 }}
                data-testid="import-result-errors-truncated"
              >
                {t('auswertungen.importDialog.errorListTruncated', {
                  count: errorRows.length - MAX_VISIBLE_ERRORS,
                })}
              </Typography>
            )}
          </Box>
        )}
      </Alert>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      data-testid="auswertungen-import-dialog"
    >
      <DialogTitle>
        {t('auswertungen.importDialog.title', { examTitle })}
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={stepIndex} sx={{ mb: 3 }}>
          <Step>
            <StepLabel>{t('auswertungen.importDialog.steps.source')}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t('auswertungen.importDialog.steps.upload')}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t('auswertungen.importDialog.steps.preview')}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t('auswertungen.importDialog.steps.confirm')}</StepLabel>
          </Step>
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="import-error">
            <AlertTitle>{error}</AlertTitle>
          </Alert>
        )}

        {step === 'source' && (
          <FormControl>
            <FormLabel>{t('auswertungen.importDialog.sourceLabel')}</FormLabel>
            <RadioGroup
              value={driverName}
              onChange={(e) => setDriverName(e.target.value as DriverName)}
              data-testid="import-driver-radio"
            >
              <FormControlLabel
                value="moodle_json"
                control={<Radio />}
                label={t('auswertungen.importDialog.sourceMoodleJson')}
              />
              <FormControlLabel
                value="moodle_api"
                control={
                  <Radio
                    disabled={moodleApiLocked || !hasMoodleConnection}
                    inputProps={{ 'data-testid': 'import-driver-moodle-api' } as React.InputHTMLAttributes<HTMLInputElement>}
                  />
                }
                label={
                  <span>
                    {t('auswertungen.importDialog.sourceMoodleApi')}
                    {moodleApiLocked ? (
                      <Box component="span" sx={{ ml: 1 }}>
                        <TierChip
                          tier={EVALUATION_UPGRADE_TIER.moodle_api}
                          locked
                          label={t('auswertungen.tierLock.moodleApiTag', {
                            tier: tierLabel(EVALUATION_UPGRADE_TIER.moodle_api),
                          })}
                          testId="import-driver-moodle-api-tier-lock"
                        />
                      </Box>
                    ) : !hasMoodleConnection && (
                      <Chip
                        label={t('auswertungen.importDialog.unavailableTag')}
                        size="small"
                        color="default"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </span>
                }
              />
            </RadioGroup>
            {moodleProbeError && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {moodleProbeError}
              </Alert>
            )}
          </FormControl>
        )}

        {step === 'upload' && driverName === 'moodle_json' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              data-testid="import-file-upload"
            >
              {t('auswertungen.importDialog.chooseFile')}
              <input
                type="file"
                hidden
                accept=".json,application/json"
                onChange={handleFileChange}
              />
            </Button>
            {file && (
              <Typography sx={{ mt: 2 }} data-testid="import-file-name">
                {file.name} ({Math.round(file.size / 1024)} KB)
              </Typography>
            )}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 3 }}
            >
              {t('auswertungen.importDialog.uploadHint')}
            </Typography>
          </Box>
        )}

        {step === 'upload' && driverName === 'moodle_api' && (
          <Box sx={{ py: 2 }}>
            <TextField
              autoFocus
              fullWidth
              label={t('auswertungen.moodleSync.quizIdLabel')}
              helperText={t('auswertungen.moodleSync.quizIdHelper')}
              value={quizIdRaw}
              onChange={(e) => setQuizIdRaw(e.target.value)}
              inputProps={{
                'data-testid': 'import-quiz-id',
                inputMode: 'numeric',
              }}
            />
          </Box>
        )}

        {step === 'preview' && preview && (
          <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Chip
                color="primary"
                label={t('auswertungen.importDialog.studentCount', {
                  count: preview.student_count,
                })}
                data-testid="preview-student-count"
              />
              <Chip
                color="secondary"
                label={t('auswertungen.importDialog.attemptCount', {
                  count: preview.attempt_count,
                })}
                data-testid="preview-attempt-count"
              />
              {preview.errors.length > 0 && (
                <Chip
                  color="warning"
                  label={t('auswertungen.importDialog.errorCount', {
                    count: preview.errors.length,
                  })}
                />
              )}
              {preview.truncated && (
                <Chip
                  color="default"
                  label={t('auswertungen.importDialog.truncatedTag')}
                  data-testid="preview-truncated"
                />
              )}
            </Box>

            {preview.warnings.length > 0 && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                data-testid="preview-warnings"
              >
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {preview.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </Alert>
            )}

            {preview.errors.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }} data-testid="preview-errors">
                <Typography variant="subtitle2">
                  {t('auswertungen.importDialog.errorListTitle')}
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {preview.errors.slice(0, MAX_VISIBLE_ERRORS).map((e) => (
                    <li key={`${e.row_index}-${e.reason}`}>
                      {t('auswertungen.importDialog.rowError', {
                        row: e.row_index,
                        reason: importRowErrorMessage(e, t),
                      })}
                    </li>
                  ))}
                </ul>
                {preview.errors.length > MAX_VISIBLE_ERRORS && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    {t('auswertungen.importDialog.errorListTruncated', {
                      count: preview.errors.length - MAX_VISIBLE_ERRORS,
                    })}
                  </Typography>
                )}
              </Alert>
            )}

            <TableContainer component={Paper} variant="outlined">
              <Table size="small" data-testid="preview-students-table">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t('auswertungen.importDialog.colExternalId')}
                    </TableCell>
                    <TableCell>
                      {t('auswertungen.importDialog.colDisplayName')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.students.map((s) => (
                    <TableRow key={s.external_id}>
                      <TableCell>{s.external_id}</TableCell>
                      <TableCell>{s.display_name ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {step === 'submitting' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            {!job || !isTerminalStatus(job.status) ? (
              // queued / running: grading happens in the worker (TF-412) —
              // show live n/total progress (TF-428) and make clear the import
              // continues in the background so the user can close the dialog.
              <>
                {job && job.graded_total != null && job.graded_total > 0 ? (
                  <Box sx={{ px: { xs: 0, sm: 4 } }} data-testid="import-grading-progress">
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(
                        100,
                        Math.round(
                          (job.graded_done / job.graded_total) * 100,
                        ),
                      )}
                    />
                    <Typography sx={{ mt: 2 }}>
                      {t('auswertungen.importDialog.gradingProgress', {
                        done: job.graded_done,
                        total: job.graded_total,
                      })}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <CircularProgress />
                    <Typography sx={{ mt: 2 }}>
                      {t('auswertungen.importDialog.submitting')}
                    </Typography>
                  </>
                )}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  {t('auswertungen.importDialog.backgroundHint')}
                </Typography>
              </>
            ) : (
              renderJobResult(job)
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {(() => {
          // TF-428: while the import runs (queued/running), the close button
          // stays enabled so the user can leave it to finish in the
          // background instead of being pinned to the spinner. handleClose
          // aborts only the client poll — the worker keeps grading, and the
          // Auswertungen status surface then tracks the job.
          const runningImport =
            step === 'submitting' && (!job || !isTerminalStatus(job.status));
          return (
            <Button
              onClick={handleClose}
              disabled={busy && !runningImport}
              data-testid="import-close"
            >
              {runningImport
                ? t('auswertungen.importDialog.runInBackground')
                : job
                  ? t('common.close')
                  : t('common.cancel')}
            </Button>
          );
        })()}

        {step === 'source' && (
          <Button
            variant="contained"
            onClick={() => setStep('upload')}
            data-testid="import-next-source"
          >
            {t('common.next')}
          </Button>
        )}

        {step === 'upload' && (
          <Button
            variant="contained"
            disabled={
              busy ||
              (driverName === 'moodle_json'
                ? !file
                : !quizIdRaw || Number(quizIdRaw) <= 0)
            }
            onClick={runPreview}
            data-testid="import-run-preview"
          >
            {busy ? <CircularProgress size={20} /> : t('common.next')}
          </Button>
        )}

        {step === 'preview' && (
          <>
            <Button
              onClick={() => {
                setStep('upload');
                setPreview(null);
              }}
              disabled={busy}
            >
              {t('common.back')}
            </Button>
            <Button
              variant="contained"
              onClick={runCommit}
              disabled={busy}
              data-testid="import-confirm"
            >
              {t('auswertungen.importDialog.confirm')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
